import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Header-attached search panel (M+G). Replaces the centered dialog modal.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 * @property {HTMLElement} [backdrop]
 * @property {HTMLInputElement} [searchInput]
 *
 * @extends {Component<Refs>}
 */
class MGHeaderSearch extends Component {
  requiredRefs = ['panel'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #emptyTimer = null;

  /** @type {HTMLElement[]} */
  #triggers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-open');

    this.#triggers = Array.from(document.querySelectorAll('[data-mg-header-search-trigger]'));
    this.#syncTriggers(false);

    document.addEventListener('keydown', this.#onDocumentKeyDown, { signal });
    this.#keepSearchOnStorefront();

    const input = this.querySelector('#mg-header-search-input, input[type="search"]');
    const dropdown = this.querySelector('#nosto-autocomplete');
    input?.addEventListener('input', () => this.#syncEmptyMessage(), { signal });
    if (dropdown) {
      new MutationObserver(() => this.#syncEmptyMessage()).observe(dropdown, {
        childList: true,
        subtree: true,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    if (this.#emptyTimer != null) window.clearTimeout(this.#emptyTimer);
    unlockScroll(this);
  }

  get isOpen() {
    return this.getAttribute('data-open') === 'true';
  }

  open = () => {
    if (this.isOpen) {
      this.#syncQueryFromUrl();
      this.#focusInput();
      return;
    }

    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('data-open', 'true');
    document.documentElement.setAttribute('data-mg-header-search-open', 'true');
    lockScroll(this);
    this.#syncTriggers(true);

    this.#syncQueryFromUrl();
    requestAnimationFrame(() => {
      this.#focusInput();
    });
  };

  close = () => {
    if (!this.isOpen) return;

    // Keep query text — Nosto Autocomplete / SERP still need the last term when reopening.
    // Clearing is only via the explicit clear control, not panel close.
    const input =
      this.refs.searchInput ||
      /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="search"], input[name="q"]'));
    const savedQuery = input?.value ?? '';

    this.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-open');
    document.documentElement.removeAttribute('data-mg-header-search-open');
    unlockScroll(this);
    this.#syncTriggers(false);

    if (input && savedQuery && input.value !== savedQuery) {
      input.value = savedQuery;
    }
  };

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * View all submits the header form. Nosto then navigates to /search, and
   * Shopify sends that unprefixed URL to the market's primary language
   * (/zh-hk → /en-hk, /fr-eu /de-eu /fr-ca → /en-eu).
   * Capture the submit and keep the prefix from the current path.
   */
  #keepSearchOnStorefront() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener(
      'submit',
      (event) => {
        if (form.dataset.mgSearchRedirect) return;

        const field = form.querySelector('input[name="q"]');
        const query = field instanceof HTMLInputElement ? field.value.trim() : '';
        if (!query) return;

        const prefix = (window.location.pathname.match(/^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i) || [''])[0];
        const searchPath = `${prefix}/search`;
        if (window.location.pathname.replace(/\/$/, '') === searchPath) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const nostojs = window.nostojs;
        if (typeof nostojs === 'function') {
          nostojs((api) => {
            if (typeof api.recordSearchSubmit === 'function') api.recordSearchSubmit(query);
          });
        }

        const url = new URL(searchPath, window.location.origin);
        url.searchParams.set('q', query);
        window.location.assign(url.href);
      },
      { capture: true, signal: this.#controller.signal }
    );
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.metaKey && event.key === 'k') {
      event.preventDefault();
      this.toggle();
    }
  };

  #focusInput() {
    const input =
      this.refs.searchInput ||
      /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="search"], input[name="q"]'));
    input?.focus();
  }

  /**
   * Keep the visible input in sync with the SERP query when Nosto still has hits.
   */
  #syncQueryFromUrl() {
    const input =
      this.refs.searchInput ||
      /** @type {HTMLInputElement | null} */ (this.querySelector('#mg-header-search-input, input[type="search"], input[name="q"]'));
    if (!input) return;

    const q = new URLSearchParams(window.location.search).get('q')?.trim() || '';
    if (q && !input.value.trim()) {
      input.value = q;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Nosto omits response.query on a miss, so the template message never mounts.
   * Show the same line from the typed value until that template message is present.
   */
  #syncEmptyMessage() {
    const empty = this.querySelector('[data-mg-search-empty]');
    const input = this.querySelector('#mg-header-search-input, input[type="search"]');
    const dropdown = this.querySelector('#nosto-autocomplete');
    if (!(empty instanceof HTMLElement) || !(input instanceof HTMLInputElement) || !dropdown) return;

    const typed = input.value.trim();
    const nostoAlreadyShowsIt = !!dropdown.querySelector('[role="status"]');
    const hasProducts = !!dropdown.querySelector('nosto-dynamic-card, .mg-nosto-product-card');
    const hasTrending = !!dropdown.querySelector('[data-nosto-element="trending"]');

    if (this.#emptyTimer != null) window.clearTimeout(this.#emptyTimer);

    if (typed.length < 2 || nostoAlreadyShowsIt || hasProducts || hasTrending) {
      empty.hidden = true;
      empty.textContent = '';
      return;
    }

    this.#emptyTimer = window.setTimeout(() => {
      const still = input.value.trim();
      const nostoHasMessage = !!dropdown.querySelector('[role="status"]');
      const stillHasProducts = !!dropdown.querySelector('nosto-dynamic-card, .mg-nosto-product-card');
      const stillTrending = !!dropdown.querySelector('[data-nosto-element="trending"]');
      if (still.length < 2 || nostoHasMessage || stillHasProducts || stillTrending) return;

      empty.hidden = false;
      empty.textContent = `No results found for '${still}'`;
    }, 500);
  }

  /**
   * @param {boolean} open
   */
  #syncTriggers(open) {
    for (const trigger of this.#triggers) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }
}

if (!customElements.get('mg-header-search')) {
  customElements.define('mg-header-search', MGHeaderSearch);
}
